/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Dropdown,
    Field,
    Input,
    makeStyles,
    Option,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    tokens
} from '@fluentui/react-components';
import { Add24Regular, DeleteRegular, Dismiss24Regular, Edit24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ExcelExportButton from '../../components/ExcelExportButton';
import type { FoerdergeberItem } from '../../models/FoerdergeberItem';
import type { IDataItem } from '../../models/IDataItem';
import { DataService } from '../../services/dataService';
import type { ExcelExportColumn } from '../../utils/excelExport';

const EXCEL_COLUMNS: ExcelExportColumn<FoerdergeberItem>[] = [
    { header: 'Name (DE)', value: (item) => item.bezeichnung, width: 50 },
    { header: 'Name (EN)', value: (item) => item.bezeichnungEn, width: 50 },
    { header: 'Source of Funds', value: (item) => item.mittelherkunftBezeichnung, width: 30 },
];

type SortDirection = 'asc' | 'desc' | null;
type SortColumn = keyof FoerdergeberItem | null;

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: tokens.spacingHorizontalL,
        gap: tokens.spacingVerticalL,
    },
    headerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: tokens.spacingHorizontalL,
    },
    filterContainer: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        alignItems: 'center',
        flex: 1,
    },
    searchInput: {
        flex: 1,
        maxWidth: '400px',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        minHeight: '300px',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        flex: 1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
    },
    tableRow: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    actionButtons: {
        display: 'flex',
        gap: tokens.spacingHorizontalXS,
    },
    colActions: {
        width: '100px',
        minWidth: '100px',
    },
    colId: {
        width: '80px',
        minWidth: '80px',
    },
    colBezeichnung: {
        width: '250px',
        minWidth: '200px',
    },
    colBezeichnungEn: {
        width: '250px',
        minWidth: '200px',
    },
    colMittelherkunft: {
        width: '200px',
        minWidth: '150px',
    },
    // Drawer styles
    section: {
        marginBottom: tokens.spacingVerticalL,
    },
    sectionTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        marginBottom: tokens.spacingVerticalM,
        color: tokens.colorNeutralForeground1,
    },
    formGroup: {
        display: 'grid',
        gap: tokens.spacingVerticalM,
        marginBottom: tokens.spacingVerticalL,
    },
    fieldRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingHorizontalM,
    },
    fullWidth: {
        gridColumn: '1 / -1',
    },
    footerActions: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        justifyContent: 'flex-end',
        paddingTop: tokens.spacingVerticalL,
        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    },
});

interface DrawerState {
    isOpen: boolean;
    item: FoerdergeberItem | null;
    isEditMode: boolean;
}

const FoerdergeberTab: React.FC = () => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();

    const [items, setItems] = useState<FoerdergeberItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [sortColumn, setSortColumn] = useState<SortColumn>('bezeichnung');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [mittelherkunftItems, setMittelherkunftItems] = useState<IDataItem[]>([]);
    const [drawerState, setDrawerState] = useState<DrawerState>({
        isOpen: false,
        item: null,
        isEditMode: false,
    });
    const [formData, setFormData] = useState<FoerdergeberItem>({
        id: 0,
        bezeichnung: '',
        bezeichnungEn: '',
        mittelherkunftId: 0,
        mittelherkunftBezeichnung: '',
    });
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<FoerdergeberItem | null>(null);

    const dataService = React.useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch all items
    useEffect(() => {
        const fetchItems = async () => {
            if (!dataService) {
                return;
            }

            try {
                setIsLoading(true);
                const data = await dataService.getProjektFoerdergeberItems();
                console.log('Fetched Foerdergeber:', data);
                setItems(data || []);
            } catch (err) {
                console.error('Error loading Foerdergeber:', err);
                setItems([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchItems();
    }, [dataService]);

    // Fetch Mittelherkunft items for dropdown
    useEffect(() => {
        const fetchMittelherkunft = async () => {
            if (!dataService) {
                return;
            }

            try {
                const data = await dataService.getMittelherkunftsItems();
                setMittelherkunftItems(data || []);
            } catch (err) {
                console.error('Error loading Mittelherkunft items:', err);
                setMittelherkunftItems([]);
            }
        };

        fetchMittelherkunft();
    }, [dataService]);

    // Filter items based on search text
    const filteredItems = useMemo(() => {
        let results = items;

        // Apply search filter
        if (filterText.trim()) {
            const searchLower = filterText.toLowerCase();
            results = results.filter((item) => {
                return (
                    item.bezeichnung?.toLowerCase().includes(searchLower) ||
                    item.bezeichnungEn?.toLowerCase().includes(searchLower) ||
                    item.mittelherkunftBezeichnung?.toLowerCase().includes(searchLower)
                );
            });
        }

        // Apply sorting
        if (sortColumn) {
            results = [...results].sort((a, b) => {
                let aValue: any = a[sortColumn];
                let bValue: any = b[sortColumn];

                // Handle null/undefined
                if (aValue == null) aValue = '';
                if (bValue == null) bValue = '';

                // Convert to lowercase for string comparison
                if (typeof aValue === 'string') aValue = aValue.toLowerCase();
                if (typeof bValue === 'string') bValue = bValue.toLowerCase();

                // Comparison
                if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return results;
    }, [items, filterText, sortColumn, sortDirection]);

    const handleSort = useCallback((column: SortColumn) => {
        if (sortColumn === column) {
            // Toggle direction or clear sort
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortColumn('bezeichnung');
                setSortDirection('asc');
            }
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    }, [sortColumn, sortDirection]);

    const handleAddNew = () => {
        setFormData({
            id: 0,
            bezeichnung: '',
            bezeichnungEn: '',
            mittelherkunftId: 0,
            mittelherkunftBezeichnung: '',
        });
        setDrawerState({
            isOpen: true,
            item: null,
            isEditMode: false,
        });
    };

    const handleView = (item: FoerdergeberItem) => {
        setFormData(item);
        setDrawerState({
            isOpen: true,
            item: item,
            isEditMode: false,
        });
    };

    const handleEdit = (item: FoerdergeberItem) => {
        setFormData(item);
        setDrawerState({
            isOpen: true,
            item: item,
            isEditMode: true,
        });
    };

    const handleRowDoubleClick = (item: FoerdergeberItem) => {
        handleView(item);
    };

    const handleDrawerClose = () => {
        setDrawerState({
            isOpen: false,
            item: null,
            isEditMode: false,
        });
        setFormData({
            id: 0,
            bezeichnung: '',
            bezeichnungEn: '',
            mittelherkunftId: 0,
            mittelherkunftBezeichnung: '',
        });
    };

    const handleDrawerSave = async () => {
        if (!dataService) return;

        try {
            setIsSaving(true);

            if (drawerState.isEditMode && drawerState.item) {
                // Update existing
                await dataService.updateProjektFoerdergeber(formData);
                setItems((prevItems) =>
                    prevItems.map((p) => (p.id === formData.id ? formData : p))
                );
            } else {
                // Add new
                await dataService.addProjektFoerdergeber(formData);
                // Reload the list
                const data = await dataService.getProjektFoerdergeberItems();
                setItems(data || []);
            }

            handleDrawerClose();
        } catch (err) {
            console.error('Error saving Foerdergeber:', err);
            alert('Error saving Foerdergeber: ' + (err instanceof Error ? err.message : 'Unknown error'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteClick = (item: FoerdergeberItem) => {
        setItemToDelete(item);
        setShowDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (!dataService || !itemToDelete) return;

        try {
            setIsSaving(true);
            await dataService.deleteProjektFoerdergeber(itemToDelete.id);

            // Remove the item from the list
            setItems((prevItems) =>
                prevItems.filter((p) => p.id !== itemToDelete.id)
            );

            setShowDeleteDialog(false);
            setItemToDelete(null);
        } catch (err) {
            console.error('Error deleting Foerdergeber:', err);
            alert('Error deleting Foerdergeber: ' + (err instanceof Error ? err.message : 'Unknown error'));
        } finally {
            setIsSaving(false);
        }
    };

    const isFormValid = useCallback(() => {
        // Bezeichnung (German name) is always required
        if (!formData.bezeichnung || formData.bezeichnung.trim() === '') {
            return false;
        }

        // BezeichnungEn (English name) is always required
        if (!formData.bezeichnungEn || formData.bezeichnungEn.trim() === '') {
            return false;
        }

        // Mittelherkunft (source of funds) is always required
        if (!formData.mittelherkunftId || formData.mittelherkunftId <= 0) {
            return false;
        }

        return true;
    }, [formData]);

    return (
        <div className={styles.container}>
            {/* Header with Filter and Add Button */}
            <div className={styles.headerContainer}>
                <div className={styles.filterContainer}>
                    <Input
                        placeholder="Search by name or source of funds..."
                        value={filterText}
                        onChange={(_, data) => setFilterText(data.value)}
                        className={styles.searchInput}
                    />
                    <ExcelExportButton
                        items={filteredItems}
                        columns={EXCEL_COLUMNS}
                        fileName={`FundingProviders_${new Date().toISOString().split('T')[0]}`}
                        sheetName="Funding Providers"
                    />
                </div>
                <Button
                    icon={<Add24Regular />}
                    appearance="primary"
                    onClick={handleAddNew}
                >
                    Add Foerdergeber
                </Button>
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading Foerdergeber..." />
                </div>
            )}

            {/* Empty State */}
            {!isLoading && items.length === 0 && (
                <div className={styles.emptyState}>
                    No Foerdergeber found. Click "Add Foerdergeber" to create one.
                </div>
            )}

            {/* Items Table */}
            {!isLoading && filteredItems.length > 0 && (
                <div className={styles.tableContainer}>
                    <Table size="small">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell className={styles.colActions}>
                                    Actions
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className={styles.colBezeichnung}
                                    onClick={() => handleSort('bezeichnung')}
                                >
                                    Name (DE)
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className={styles.colBezeichnungEn}
                                    onClick={() => handleSort('bezeichnungEn')}
                                >
                                    Name (EN)
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className={styles.colMittelherkunft}
                                    onClick={() => handleSort('mittelherkunftBezeichnung')}
                                >
                                    Source of Funds
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredItems.map((item) => (
                                <TableRow
                                    key={item.id}
                                    className={styles.tableRow}
                                    onDoubleClick={() => handleRowDoubleClick(item)}
                                >
                                    <TableCell>
                                        <TableCellLayout>
                                            <div className={styles.actionButtons}>
                                                <Button
                                                    icon={<Edit24Regular />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEdit(item);
                                                    }}
                                                    appearance="subtle"
                                                    size="small"
                                                    title="Edit"
                                                />
                                                <Button
                                                    icon={<DeleteRegular />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteClick(item);
                                                    }}
                                                    appearance="subtle"
                                                    size="small"
                                                    title="Delete"
                                                />
                                            </div>
                                        </TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.bezeichnung || '-'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.bezeichnungEn || '-'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.mittelherkunftBezeichnung || '-'}</TableCellLayout>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* No results after filtering */}
            {!isLoading && items.length > 0 && filteredItems.length === 0 && (
                <div className={styles.emptyState}>
                    No Foerdergeber match your search
                </div>
            )}

            {/* Drawer for Add/Edit */}
            <Drawer
                open={drawerState.isOpen}
                onOpenChange={(_, { open }) => {
                    if (!open) handleDrawerClose();
                }}
                size="medium"
                type="overlay"
                position="end"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="Close"
                                icon={<Dismiss24Regular />}
                                onClick={handleDrawerClose}
                            />
                        }
                    >
                        {drawerState.isEditMode ? 'Edit Foerdergeber' : drawerState.item ? 'View Foerdergeber' : 'Add Foerdergeber'}
                    </DrawerHeaderTitle>
                </DrawerHeader>

                <DrawerBody>
                    <div className={styles.section}>
                        <div className={styles.formGroup}>
                            <Field label="Name (German)" required>
                                <Input
                                    value={formData.bezeichnung || ''}
                                    onChange={(_, data) =>
                                        setFormData({ ...formData, bezeichnung: data.value })
                                    }
                                    disabled={!drawerState.isEditMode && drawerState.item !== null}
                                    placeholder="Enter German name"
                                />
                            </Field>
                            <Field label="Name (English)" required>
                                <Input
                                    value={formData.bezeichnungEn || ''}
                                    onChange={(_, data) =>
                                        setFormData({ ...formData, bezeichnungEn: data.value })
                                    }
                                    disabled={!drawerState.isEditMode && drawerState.item !== null}
                                    placeholder="Enter English name"
                                />
                            </Field>
                            <Field label="Source of Funds" required>
                                <Dropdown
                                    value={formData.mittelherkunftBezeichnung || ''}
                                    onOptionSelect={(_, data) => {
                                        const selectedItem = mittelherkunftItems.find(item => item.text === data.optionValue);
                                        setFormData({
                                            ...formData,
                                            mittelherkunftId: selectedItem ? parseInt(selectedItem.value) : 0,
                                            mittelherkunftBezeichnung: selectedItem?.text || ''
                                        });
                                    }}
                                    disabled={!drawerState.isEditMode && drawerState.item !== null}
                                    placeholder="Select source of funds"
                                >
                                    {mittelherkunftItems.map((item) => (
                                        <Option key={item.value} value={item.text}>
                                            {item.text}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </Field>
                        </div>

                        {/* Footer Actions */}
                        <div className={styles.footerActions}>
                            <Button
                                appearance="secondary"
                                onClick={handleDrawerClose}
                                disabled={isSaving}
                            >
                                {drawerState.isEditMode || !drawerState.item ? 'Cancel' : 'Close'}
                            </Button>
                            {(drawerState.isEditMode || !drawerState.item) && (
                                <Button
                                    appearance="primary"
                                    onClick={handleDrawerSave}
                                    disabled={isSaving || !isFormValid()}
                                >
                                    {isSaving ? <Spinner size="tiny" /> : 'Save'}
                                </Button>
                            )}
                        </div>
                    </div>
                </DrawerBody>
            </Drawer>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={(_, data) => setShowDeleteDialog(data.open)}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>
                            Delete Foerdergeber
                        </DialogTitle>
                        <DialogContent>
                            Are you sure you want to delete this Foerdergeber{' '}
                            <strong>{itemToDelete?.bezeichnung}</strong>?
                        </DialogContent>
                        <div
                            style={{
                                display: 'flex',
                                gap: tokens.spacingHorizontalM,
                                justifyContent: 'flex-end',
                                paddingTop: tokens.spacingVerticalL,
                            }}
                        >
                            <Button
                                appearance="secondary"
                                onClick={() => setShowDeleteDialog(false)}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                appearance="primary"
                                onClick={handleConfirmDelete}
                                disabled={isSaving}
                            >
                                {isSaving ? <Spinner size="tiny" /> : 'Delete'}
                            </Button>
                        </div>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

export default FoerdergeberTab;
